import { Component, Input, Output, EventEmitter, OnInit, signal, HostListener, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/services/auth.service';

import { NgIcon, provideIcons } from '@ng-icons/core';

import {
  hugeBuilding05,
  hugeCalendar03,
  hugeCarParking01,
  hugeCpuCharge,
  hugeElectricPlugs,
  hugeEvCharging,
  hugeGlobe02,
  hugeHealtcare,
  hugeInvestigation,
  hugeInvoice,
  hugeInvoice03,
  hugeLocation01,
  hugeMapPin,
  hugeMapsGlobal01,
  hugeMapsLocation01,
  hugeMessageNotification01,
  hugeMessageQuestion,
  hugeMoneyExchange02,
  hugeNotification02,
  hugePlugSocket,
  hugePolicy,
  hugeProductLoading,
  hugeRanking,
  hugeRealEstate02,
  hugeUser,
  hugeUserEdit01,
  hugeUserGroup,
  hugeUserIdVerification,
  hugeCoffee02
} from '@ng-icons/huge-icons';

interface MenuItem {
  label: string;
  icon: string;
  route?: string;
  permission?: string; // Permiso requerido para ver el item
}

interface MenuGroup {
  title: string;
  items: MenuItem[];
}

/**
 * Menú lateral de navegación. Los elementos se filtran por el permiso `permission`
 * de cada item; las categorías sin elementos visibles se ocultan.
 */
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TooltipModule,
    NgIcon
  ],
  viewProviders: [provideIcons({
    hugeBuilding05,
    hugeCalendar03,
    hugeCarParking01,
    hugeCpuCharge,
    hugeElectricPlugs,
    hugeEvCharging,
    hugeGlobe02,
    hugeHealtcare,
    hugeInvestigation,
    hugeInvoice,
    hugeInvoice03,
    hugeLocation01,
    hugeMapPin,
    hugeMapsGlobal01,
    hugeMapsLocation01,
    hugeMessageNotification01,
    hugeMessageQuestion,
    hugeMoneyExchange02,
    hugeNotification02,
    hugePlugSocket,
    hugePolicy,
    hugeProductLoading,
    hugeRanking,
    hugeRealEstate02,
    hugeUser,
    hugeUserEdit01,
    hugeUserGroup,
    hugeUserIdVerification,
    hugeCoffee02
  })],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit {
  @Input() visible = true;
  @Output() visibleChange = new EventEmitter<boolean>();

  private authService = inject(AuthService);
  private router = inject(Router);

  isMobile = signal(false);
  appVersion = environment.appVersion;
  environmentName = environment.environmentName;
  isProduction = environment.production;

  menuCategories: MenuGroup[] = [
    {
      title: 'Operaciones y Monitoreo',
      items: [
        { label: 'Mapa de Estaciones', icon: 'hugeMapPin', route: '/stations-map', permission: 'stations-map.read' },
        { label: 'Historial de Cargas', icon: 'hugeInvoice03', route: '/charging-transactions', permission: 'charging-sessions.read' },
        { label: 'Notificaciones de Cargadores', icon: 'hugeMessageNotification01', route: '/charger-notifications', permission: 'charger-notifications.read' },
        { label: 'Reseñas', icon: 'hugeRanking', route: '/station-reviews', permission: 'station-reviews.read' }
      ]
    },
    {
      title: 'Red de Servicio',
      items: [
        { label: 'Empresas', icon: 'hugeBuilding05', route: '/companies', permission: 'companies.read' },
        { label: 'Estaciones', icon: 'hugeRealEstate02', route: '/stations', permission: 'stations.read' },
        { label: 'Cargadores', icon: 'hugeEvCharging', route: '/chargers', permission: 'chargers.read' },
        { label: 'Conectores', icon: 'hugeElectricPlugs', route: '/connectors', permission: 'connectors.read' },
        { label: 'Tipos de Conectores', icon: 'hugePlugSocket', route: '/connector-types', permission: 'connector-types.read' },
        { label: 'Niveles de carga', icon: 'hugeCpuCharge', route: '/power-ranges', permission: 'power-ranges.read' },
        { label: 'Amenidades', icon: 'hugeCoffee02', route: '/amenities', permission: 'amenities.read' },
      ]
    },
    {
      title: 'Gestión Comercial',
      items: [
        { label: 'Configuración de Precios', icon: 'hugeMoneyExchange02', route: '/price-configuration', permission: 'prices.read' },
        { label: 'Precios Faltantes', icon: 'hugeInvestigation', route: '/missing-price-configurations-report', permission: 'price-missing.read' },
        { label: 'Condiciones Comerciales', icon: 'hugeCalendar03', route: '/commercial-conditions', permission: 'commercial-conditions.read' },
        { label: 'Zonas Comerciales', icon: 'hugeMapsLocation01', route: '/commercial-zones', permission: 'commercial-zones.read' },
        { label: 'Facturas', icon: 'hugeInvoice', route: '/invoices', permission: 'invoices.read' },
        { label: 'Políticas de Reserva', icon: 'hugePolicy', route: '/reservation-policies', permission: 'reservation-policies.read' },
        { label: 'Programas de Lealtad', icon: 'hugeHealtcare', route: '/loyalty-programs', permission: 'loyalty-programs.read' },
        { label: 'Productos Yena', icon: 'hugeProductLoading', route: '/yena-products', permission: 'yena-products.read' }
      ]
    },
    {
      title: 'Clientes y Vehículos',
      items: [
        { label: 'Clientes', icon: 'hugeUserIdVerification', route: '/customers', permission: 'customers.read' },
        { label: 'Grupos de Clientes', icon: 'hugeUserGroup', route: '/customer-groups', permission: 'customer-groups.read' },
        { label: 'Modelos de Vehículos', icon: 'hugeCarParking01', route: '/vehicle-models', permission: 'vehicle-models.read' },
        { label: 'Notificaciones', icon: 'hugeNotification02', route: '/push-notifications', permission: 'push-notifications.read' }
      ]
    },
    {
      title: 'Ubicación y Geografía',
      items: [
        { label: 'Regiones', icon: 'hugeGlobe02', route: '/regions', permission: 'regions.read' },
        { label: 'Zonas', icon: 'hugeMapsGlobal01', route: '/zones', permission: 'zones.read' },
        { label: 'Códigos Postales', icon: 'hugeLocation01', route: '/postal-codes', permission: 'postal-codes.read' }
      ]
    },
    {
      title: 'Configuración y Soporte',
      items: [
        { label: 'Usuarios', icon: 'hugeUser', route: '/users', permission: 'users.read' },
        { label: 'Perfiles', icon: 'hugeUserEdit01', route: '/roles', permission: 'roles.read' },
        { label: 'Preguntas Frecuentes', icon: 'hugeMessageQuestion', route: '/faqs', permission: 'faqs.read' }
      ]
    },
  ];

  // Filtrar items del menú según permisos del usuario
  filteredMenuCategories = computed(() => {
    return this.menuCategories.map(category => ({
      ...category,
      items: category.items.filter(item =>
        !item.permission || this.authService.hasPermission(item.permission)
      )
    })).filter(category => category.items.length > 0); // Eliminar categorías vacías
  });

  ngOnInit(): void {
    this.checkScreenSize();
  }

  @HostListener('window:resize')
  onResize() {
    this.checkScreenSize();
  }

  private checkScreenSize() {
    const mobile = window.innerWidth <= 768;
    this.isMobile.set(mobile);

    if (mobile && this.visible) {
      this.closeSidebar();
    }
  }

  closeSidebar(): void {
    this.visibleChange.emit(false);
  }

  onNavigate(): void {
    if (this.isMobile()) {
      this.closeSidebar();
    }
  }

  isMenuItemActive(route?: string): boolean {
    if (!route) {
      return false;
    }

    const currentUrl = this.normalizePath(this.router.url);
    const menuRoute = this.normalizePath(route);

    return currentUrl === menuRoute || currentUrl.startsWith(`${menuRoute}/`);
  }

  private normalizePath(path: string): string {
    const cleanPath = path.split('?')[0].split('#')[0];
    return cleanPath.endsWith('/') && cleanPath.length > 1
      ? cleanPath.slice(0, -1)
      : cleanPath;
  }
}
